import { useRef, useCallback } from "react";
import type { CameraSlotConfig } from "../services/StorageService";

interface UseVideoRefsResult {
  videoRef1: React.RefObject<HTMLVideoElement | null>;
  videoRef2: React.RefObject<HTMLVideoElement | null>;
  videoRef3: React.RefObject<HTMLVideoElement | null>;
  attachStream: (index: number, stream: MediaStream) => void;
  detachStream: (index: number) => void;
  detachAllStreams: () => void;
  setEnhancementEnabled: (enabled: boolean) => void; // Deprecated, kept for backwards compatibility
}

interface UseVideoRefsParams {
  slotConfigs: [CameraSlotConfig, CameraSlotConfig, CameraSlotConfig];
}

/**
 * Custom hook for managing video element references for a fixed set of 3 cameras
 */
export const useVideoRefs = ({ slotConfigs }: UseVideoRefsParams): UseVideoRefsResult => {
  // Create 3 fixed video refs explicitly
  const videoRef1 = useRef<HTMLVideoElement>(null);
  const videoRef2 = useRef<HTMLVideoElement>(null);
  const videoRef3 = useRef<HTMLVideoElement>(null);

  // Track active streams to manage them independently
  const activeStreamsRef = useRef<(MediaStream | null)[]>([null, null, null]);
  
  // Enhancement processing state
  const processingCanvasesRef = useRef<(HTMLCanvasElement | null)[]>([null, null, null]);
  const hiddenVideosRef = useRef<(HTMLVideoElement | null)[]>([null, null, null]);
  const enhancedStreamsRef = useRef<(MediaStream | null)[]>([null, null, null]);
  const processingFrameIdsRef = useRef<(number | null)[]>([null, null, null]);

  /**
   * Apply image enhancement with slot-specific settings
   */
  const enhanceImageForDisplay = useCallback((
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    slotConfig: CameraSlotConfig
  ): void => {
    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Use slot-specific enhancement settings
      if (!slotConfig.enhancementEnabled) return;

      const contrast = slotConfig.contrast || 1;
      const brightness = slotConfig.brightness || 0;

      // Apply contrast and brightness adjustment
      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.min(255, Math.max(0, (data[i] - 128) * contrast + 128 + brightness));
        data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - 128) * contrast + 128 + brightness));
        data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - 128) * contrast + 128 + brightness));
      }

      // Apply zoom
      const zoomFactor = slotConfig.zoom || 1;
      if (zoomFactor !== 1 && canvas.width > 0 && canvas.height > 0) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
          tempCtx.putImageData(imageData, 0, 0);

          const srcW = canvas.width / zoomFactor;
          const srcH = canvas.height / zoomFactor;
          const srcX = (canvas.width - srcW) / 2;
          const srcY = (canvas.height - srcH) / 2;

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(tempCanvas, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
          return;
        }
      }

      ctx.putImageData(imageData, 0, 0);
    } catch (error) {
      console.error("Error enhancing image for display:", error);
    }
  }, []);

  /**
   * Process frames from the original stream and apply enhancement
   */
  const processFrames = useCallback((index: number) => {
    const hiddenVideo = hiddenVideosRef.current[index];
    const canvas = processingCanvasesRef.current[index];
    const videoRef = [videoRef1, videoRef2, videoRef3][index];
    const slotConfig = slotConfigs[index];

    if (!hiddenVideo || !canvas || !videoRef.current || !slotConfig.enhancementEnabled) {
      return;
    }

    if (hiddenVideo.readyState >= 2 && hiddenVideo.videoWidth > 0 && hiddenVideo.videoHeight > 0) {
      const ctx = canvas.getContext('2d', { willReadFrequently: false, alpha: false });
      if (ctx) {
        // Draw the video frame
        ctx.drawImage(hiddenVideo, 0, 0, canvas.width, canvas.height);
        
        // Apply enhancement with slot-specific settings
        enhanceImageForDisplay(ctx, canvas, slotConfig);
      }
    }

    // Continue processing
    processingFrameIdsRef.current[index] = requestAnimationFrame(() => processFrames(index));
  }, [slotConfigs, enhanceImageForDisplay]);

  /**
   * Start enhancement processing for a stream
   */
  const startEnhancementProcessing = useCallback((index: number, stream: MediaStream) => {
    // Stop any existing processing
    if (processingFrameIdsRef.current[index] !== null) {
      cancelAnimationFrame(processingFrameIdsRef.current[index]!);
      processingFrameIdsRef.current[index] = null;
    }

    // Create hidden video element to receive original stream
    const hiddenVideo = document.createElement('video');
    hiddenVideo.autoplay = true;
    hiddenVideo.muted = true;
    hiddenVideo.playsInline = true;
    hiddenVideo.srcObject = stream;
    hiddenVideo.play().catch(err => console.warn('Hidden video play failed:', err));
    hiddenVideosRef.current[index] = hiddenVideo;

    // Wait for video metadata to create canvas
    hiddenVideo.onloadedmetadata = () => {
      // Create canvas for processing
      const canvas = document.createElement('canvas');
      canvas.width = hiddenVideo.videoWidth;
      canvas.height = hiddenVideo.videoHeight;
      processingCanvasesRef.current[index] = canvas;

      // Create enhanced stream from canvas
      const enhancedStream = canvas.captureStream(30); // 30 FPS
      enhancedStreamsRef.current[index] = enhancedStream;

      // Attach enhanced stream to the visible video element
      const videoRef = [videoRef1, videoRef2, videoRef3][index];
      if (videoRef.current) {
        videoRef.current.srcObject = enhancedStream;
        videoRef.current.play().catch(err => console.warn('Enhanced video play failed:', err));
      }

      // Start processing frames
      processFrames(index);
    };
  }, [processFrames]);

  /**
   * Stop enhancement processing for a stream
   */
  const stopEnhancementProcessing = useCallback((index: number) => {
    // Stop animation frame
    if (processingFrameIdsRef.current[index] !== null) {
      cancelAnimationFrame(processingFrameIdsRef.current[index]!);
      processingFrameIdsRef.current[index] = null;
    }

    // Clean up hidden video
    const hiddenVideo = hiddenVideosRef.current[index];
    if (hiddenVideo) {
      hiddenVideo.pause();
      hiddenVideo.srcObject = null;
      hiddenVideosRef.current[index] = null;
    }

    // Clean up enhanced stream
    const enhancedStream = enhancedStreamsRef.current[index];
    if (enhancedStream) {
      enhancedStream.getTracks().forEach(track => track.stop());
      enhancedStreamsRef.current[index] = null;
    }

    // Clean up canvas
    processingCanvasesRef.current[index] = null;
  }, []);

  /**
   * @deprecated Enhancement settings are now configured per-slot via slotConfigs.
   * This function is kept for backwards compatibility but does nothing.
   */
  const setEnhancementEnabled = useCallback(() => {
    console.warn('setEnhancementEnabled is deprecated. Enhancement settings are now configured per-slot.');
  }, []);

  // Attach a media stream to a video element
  const attachStream = useCallback((index: number, stream: MediaStream) => {
    let videoRef: React.RefObject<HTMLVideoElement | null>;

    switch (index) {
      case 0:
        videoRef = videoRef1;
        break;
      case 1:
        videoRef = videoRef2;
        break;
      case 2:
        videoRef = videoRef3;
        break;
      default:
        console.warn(`Invalid index ${index}, defaulting to camera 1`);
        videoRef = videoRef1;
        index = 0;
    }

    // Store this stream in our ref array
    activeStreamsRef.current[index] = stream;

    // Stop any existing enhancement processing for this stream
    stopEnhancementProcessing(index);

    const slotConfig = slotConfigs[index];

    if (videoRef.current) {
      try {
        // First safely detach any existing stream
        if (videoRef.current.srcObject) {
          try {
            videoRef.current.pause();
            videoRef.current.srcObject = null;
          } catch (e) {
            console.warn(`Error clearing previous srcObject: ${e}`);
          }
        }

        // Set autoplay and muted attributes
        videoRef.current.autoplay = true;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;

        // If enhancement is enabled for this slot, start processing pipeline
        if (slotConfig.enhancementEnabled) {
          console.log(`Stream attached with enhancement to video element ${index}`);
          startEnhancementProcessing(index, stream);
        } else {
          // Set new stream directly
          videoRef.current.srcObject = stream;

          // Attempt to play
          videoRef.current.play().catch((err) => {
            console.warn(
              `Autoplay failed: ${err.message}. User interaction may be needed.`
            );
          });

          console.log(`Stream successfully attached to video element ${index}`);
        }
      } catch (e) {
        console.error(`Error attaching stream to video ${index}: ${e}`);
      }
    } else {
      console.error(`Video element ${index} not available`);
    }
  }, [slotConfigs, startEnhancementProcessing, stopEnhancementProcessing]);

  // Detach stream from a video element
  const detachStream = useCallback((index: number) => {
    let videoRef: React.RefObject<HTMLVideoElement | null>;

    switch (index) {
      case 0:
        videoRef = videoRef1;
        break;
      case 1:
        videoRef = videoRef2;
        break;
      case 2:
        videoRef = videoRef3;
        break;
      default:
        console.warn(`Invalid index ${index}, defaulting to camera 1`);
        videoRef = videoRef1;
        index = 0;
    }

    // Stop enhancement processing if active
    stopEnhancementProcessing(index);

    // Get the stream from our ref array
    const stream = activeStreamsRef.current[index];

    if (stream) {
      // Stop all tracks
      try {
        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (e) {
            console.warn(`Error stopping track: ${e}`);
          }
        });
      } catch (e) {
        console.warn(`Error stopping stream tracks: ${e}`);
      }

      // Clear our reference
      activeStreamsRef.current[index] = null;
    }

    // Clear the video element
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      } catch (e) {
        console.warn(`Error clearing video element ${index}: ${e}`);
      }
    }

    console.log(`Stream detached from video element ${index}`);
  }, [stopEnhancementProcessing]);

  // Detach all streams
  const detachAllStreams = useCallback(() => {
    console.log("Detaching all streams");
    [0, 1, 2].forEach((index) => detachStream(index));
  }, [detachStream]);

  return {
    videoRef1,
    videoRef2,
    videoRef3,
    attachStream,
    detachStream,
    detachAllStreams,
    setEnhancementEnabled,
  };
};
